import {Link, Token, type TokenProps} from '@primer/react'
import {type ElementType, useRef, useState} from 'react'
import styles from './IssueTypeToken.module.css'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'

export type IssueTypeTokenProps = {
  name: string
  color: string
  getTooltipText: (isTextTruncated: boolean) => string | undefined
  className?: string
  /**
   * Element used for the token's link. Defaults to a Primer anchor rendered
   * from `href`. Pass a client-router link component (e.g. from
   * `@github-ui/react-router`) for in-app SPA navigation with a real,
   * keyboard-accessible link; provide its destination via `to`.
   */
  as?: ElementType
  href?: string
  to?: string
} & Pick<TokenProps, 'size'>

export const IssueTypeToken = ({name, color, href, to, as, getTooltipText, size, className}: IssueTypeTokenProps) => {
  const tokenRef = useRef<HTMLElement>(null)
  const [tooltipText, setTooltipText] = useState<string | undefined>(undefined)

  // Defer the truncation check to hover/focus time instead of measuring on mount.
  // Reading scrollWidth/clientWidth forces a layout reflow — doing this eagerly for
  // every row in a list is expensive. Since the tooltip only appears on interaction,
  // we can wait until then to measure.
  const checkTruncation = () => {
    setTooltipText(getTooltipText(isTextTruncated(tokenRef.current)))
  }

  const token = (
    <Token
      className={clsx(styles.token, className)}
      data-color={color}
      ref={tokenRef}
      // the span in the text is allowing us to check if the text is truncated
      text={<span className={styles.tokenText}>{name}</span>}
    />
  )

  // When `as` is provided (e.g. a client-router link) it navigates via `to`;
  // otherwise render the default Primer anchor from `href`. Both are real,
  // keyboard-operable links. The custom link gets a style reset so it matches
  // the Primer anchor (no default link color/underline around the token pill).
  const LinkComponent = as
  const internal = LinkComponent ? (
    <LinkComponent to={to} className={styles.customLink}>
      {token}
    </LinkComponent>
  ) : (
    <Link href={href}>{token}</Link>
  )

  return (
    <div
      className={styles.issueTypeTokenContainer}
      data-size={size}
      onPointerEnter={checkTruncation}
      onFocus={checkTruncation}
    >
      {tooltipText ? <Tooltip text={tooltipText}>{internal}</Tooltip> : internal}
    </div>
  )
}
// This is not bullet proof, since the true inner width of the element is rounded down.
// I think it's pretty good though. See discussion here https://stackoverflow.com/questions/64689074/how-to-check-if-text-is-truncated-by-css-using-javascript
const isTextTruncated = (e: HTMLElement | null): boolean => {
  if (!e || !e.firstElementChild) return false
  return e.firstElementChild.scrollWidth > e.firstElementChild.clientWidth
}
